import { forwardRef, useImperativeHandle, useRef } from "react";
import { useParams } from "react-router-dom";
import chatApi from "../../../../../api/chatApi";
import { PaperPlaneIcon } from "../../../../../common/Icons/PaperPlaneIcon";
import { useBrightbotStore } from "../../../../../store/useBrightbotStore";
import {
  useSessionActions,
  useSessionStore,
} from "../../../../../store/useSessionStore";

import { StyledStopIcon } from "../../style";
import {
  ActionsWrapper,
  ButtonSubmit,
  EditorWrapper,
  FormField,
  FormWrapper,
} from "./style";

type Props = {
  onSubmit: (e: any) => Promise<void>;
};

const ChatField = forwardRef<HTMLButtonElement, Props>(({ onSubmit }, ref) => {
  const { threadId } = useParams();
  const activeRunId = useSessionStore.use.activeRunId();
  const messageValue = useSessionStore.use.messageValue();
  const streaming = useSessionStore.use.streaming();
  const thread = useSessionStore.use.thread();
  const {
    setActiveRunId,
    setMessageValue,
    setStream,
    setInterruptAction,
    clearSteps,
  } = useSessionActions();
  const activeAssistant = useBrightbotStore.use.activeAssistant();
  const innerRef = useRef<HTMLButtonElement>(null);

  const interruptAction = useSessionStore.use.interruptAction();

  const abortMessageHandler = async () => {
    if (!activeRunId) return;
    const currentThread = thread || threadId;

    // stop Interrupt Action if available and clear previous steps
    clearSteps();
    if (interruptAction) {
      setInterruptAction(null);
    }
    try {
      await chatApi.post(
        `/threads/${currentThread}/runs/${activeRunId}/cancel?wait=false&action=interrupt`
      );
    } catch (err) {
      console.log(err);
    } finally {
      setStream(false);
      setActiveRunId(undefined);
    }
  };

  useImperativeHandle(ref, () => innerRef.current as HTMLButtonElement);

  return (
    <EditorWrapper>
      <form onSubmit={onSubmit}>
        <FormWrapper>
          <FormField
            id="chatId"
            placeholder="Ask something&hellip;"
            disabled={!!interruptAction}
            name="message"
            value={messageValue}
            onChange={(e) => setMessageValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && e.shiftKey === false) {
                e.preventDefault();
                if (!streaming) {
                  innerRef?.current?.click();
                  setMessageValue("");
                }
              }
            }}
            multiline
          />
          <ActionsWrapper>
            {streaming ? (
              <ButtonSubmit disabled={!activeAssistant}>
                <StyledStopIcon onClick={abortMessageHandler} />
              </ButtonSubmit>
            ) : (
              <ButtonSubmit
                disabled={!activeAssistant || !!interruptAction}
                type="submit"
                ref={innerRef}
              >
                <PaperPlaneIcon />
              </ButtonSubmit>
            )}
          </ActionsWrapper>
        </FormWrapper>
      </form>
    </EditorWrapper>
  );
});

export default ChatField;
